import { IPageContent } from "api/interfaces/static-content.type";
import PAImage from "component/design-system/images/PAImage";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import MakeModelLinks from "modules/product-overview/MakeModelLinks";
import ProductDescriptionFromStaticContent from "modules/product/ProductDescriptionFromStaticContent";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import Link from "next/link";
import { renderHTML } from "util/DomHelper";
import HideIf from "util/HideIf";
import { isKit } from "util/product/is-kit";
import SafeLink from "../../../../../modules/components/SafeLink";
import { getRelativeImagePath } from "../../../../../util/helper/getRelativeImagePath";
import { isEmpty } from "../../../../../util/lodash/isEmpty";
import JsonExtendedDescription from "../json-extended-description/JsonExtendedDescription";

const ProductExtendedDescription = dynamic(() => import(/* webpackChunkName: "product-extended-description" */ "modules/extended-desciption/ProductExtendedDescription"));
const KitComponents = dynamic(() => import(/* webpackChunkName: "kit-components" */ "modules/shopping-cart/products/KitComponents"));

interface IProps{
  staticContent: IPageContent;
}

function MainOverview({
  product = {},
  details,
  contentIds,
  staticContent,
  handleProductOverviewSectionExpand,
  manufacturer,
  manufactureImageUrl,
  manufacturerPageUrl,
}: IProps) {
  const { t } = useTranslation("product");

  const extDesc = !isEmpty(product.extendedDescription);
  const extDescDescriptions = extDesc
    ? !isEmpty(product.extendedDescription.descriptions)
    : false;
  const manufacturerUrl = !isEmpty(details.manufacturerUrl);
  const manufacturerWarrantyUrl = !isEmpty(
    details.manufacturerWarrantyUrl,
  );
  const manufacturerWarrantyComment = !isEmpty(
    details.manufacturerWarrantyComment,
  );
  const manufactureImagePath = getRelativeImagePath(manufactureImageUrl);
  const { featuresAndBenefits } = staticContent;
  const isFeaturesAndBenefitsNodeExists = featuresAndBenefits !== undefined && staticContent.featuresAndBenefits.length !== 0;
  const isKitProduct = isKit(product);

  if (!(
    (extDesc && extDescDescriptions)
    || manufacturerUrl
    || manufacturerWarrantyUrl
    || manufacturerWarrantyComment
    || manufactureImagePath
  )) {
    return null;
  }
  return (
    <div>
      <HideIf show={isKitProduct}>
        <KitComponents product={product} showQuantity productQuantity={1} showComponentDescription />
      </HideIf>
      <JsonExtendedDescription product={product} />
      {manufactureImagePath && (
        <div className="image-boxed" style={{ paddingBottom: "calc(1 * var(--safari-root-font, 10px))" }}>

          <h4><strong>{t("Product Information")}</strong></h4>

          <SafeLink href={manufacturerPageUrl}>
            <PAImage
              style={{ paddingTop: "calc(1 * var(--safari-root-font, 10px))" }}
              src={manufactureImagePath}
              width={130}
              height={37}
              alt={manufacturer}
            />
          </SafeLink>
        </div>
      )}

      <HideIf show={!isKitProduct}>
        <ProductDescriptionFromStaticContent
          isFeaturesAndBenefitsNodeExists={isFeaturesAndBenefitsNodeExists}
          contentIds={contentIds}
          extendedDescription={product.extendedDescription}
        />
      </HideIf>

      {extDesc && extDescDescriptions && (
        <div>

          <h4>

            <strong>Product&apos;s Extended Description</strong>
          </h4>

          <div>

            <ProductExtendedDescription
              data={product.extendedDescription.descriptions}
            />
          </div>
        </div>
      )}

      {manufacturerUrl && (
        <>

          <Link href={manufacturerPageUrl || details.manufacturerUrl} target="_blank" rel="nofollow noreferrer">

            <h4 style={{ marginTop: "calc(3 * var(--safari-root-font, 10px))" }}>

              <strong>{product.manufacturer}</strong>
            </h4>
          </Link>

          <div className="ProductOverview-description">
            {details.manufacturerDescription}
          </div>
        </>
      )}

      {manufacturerWarrantyUrl && manufacturerWarrantyComment && (
        <div>

          <a
            href={details.manufacturerWarrantyUrl}
            target="_blank"
            rel="nofollow noreferrer"
          >

            <h4 style={{ marginTop: "calc(3 * var(--safari-root-font, 10px))" }}>

              <strong>
                {product.manufacturer}
                &apos;s Warranty
              </strong>
            </h4>
          </a>

          <div className="ProductOverview-description">
            {details.manufacturerWarrantyComment}
          </div>
        </div>
      )}

      {
        // https://dev.partsavatar.ca/flowmaster-exhaust-system-817706#makeModels
        staticContent?.links?.makeModels && <MakeModelLinks makeModels={staticContent?.links?.makeModels} handleProductOverviewSectionExpand={handleProductOverviewSectionExpand} />
      }
      <HideIf show={isFeaturesAndBenefitsNodeExists}>
        {/* to override the features and benefits coming from contentIDs */}
        <PAText fontSize={FONT_SIZE[2]} bold>{t("Features & Benefits")}</PAText>
        <div dangerouslySetInnerHTML={renderHTML(staticContent.featuresAndBenefits)} />
      </HideIf>

      <HideIf show={product.sku}>
        <PAText component="p" className="ProductOverview-description m-t-30">
          {`SKU: ${product.sku}`}
        </PAText>
      </HideIf>
    </div>
  );
}

export default MainOverview;
